/** Public accept page for a partner-team invite (/partner/join/:token).
 *  Previews the invite (reseller + role), lets the invitee set a password,
 *  then auto-logs-in and lands in the partner console. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { ApiPaths, api, scheduleProactiveRefresh } from "@/lib/api";
import { tokenStore } from "@/lib/tokenStore";

type Preview = {
  reseller_name: string;
  email: string;
  role: "administrator" | "manager" | "billing";
  first_name: string;
  last_name: string;
};

export default function PartnerJoinPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const preview = useQuery<Preview>({
    queryKey: ["partner-invite-preview", token],
    queryFn: async () =>
      (await api.get<Preview>(ApiPaths.partnerMemberInvitePreview(token))).data,
    retry: false,
    enabled: !!token,
  });

  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (preview.data) {
      setFirstName((f) => f || preview.data.first_name);
      setLastName((l) => l || preview.data.last_name);
    }
  }, [preview.data]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setErr(t("partnerJoin.passwordTooShort"));
      return;
    }
    setPending(true);
    setErr(null);
    try {
      const res = await api.post<{ access: string; refresh: string }>(
        ApiPaths.partnerMemberAccept,
        { token, password, first_name: firstName, last_name: lastName },
      );
      tokenStore.setPair(res.data.access, res.data.refresh);
      scheduleProactiveRefresh(res.data.access);
      navigate("/partner");
    } catch (e2) {
      const detail = (e2 as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setErr(detail || t("partnerJoin.error"));
      setPending(false);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="w-full max-w-md rounded-lg border border-nb-border bg-nb-surface/40 p-6">
        {preview.isLoading ? (
          <Spinner label={t("common.loading")} />
        ) : preview.isError || !preview.data ? (
          <div className="text-sm text-nb-danger" data-testid="invite-invalid">
            {t("partnerJoin.invalid")}
          </div>
        ) : (
          <>
            <h1 className="mb-1 text-lg font-semibold text-nb-text">
              {t("partnerJoin.title", { name: preview.data.reseller_name })}
            </h1>
            <p className="mb-4 text-sm text-nb-text-2">
              {t("partnerJoin.subtitle", {
                email: preview.data.email,
                role: t(`partnerTeam.role.${preview.data.role}`),
              })}
            </p>
            <form className="space-y-3" onSubmit={submit}>
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("partnerJoin.firstName")}
                  className="rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
                />
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t("partnerJoin.lastName")}
                  className="rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
                />
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("partnerJoin.password")}
                autoComplete="new-password"
                className="w-full rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
                data-testid="join-password"
              />
              <button
                type="submit"
                className="btn-primary w-full text-sm"
                disabled={pending}
                data-testid="join-submit"
              >
                {pending ? t("partnerJoin.joining") : t("partnerJoin.join")}
              </button>
              {err && (
                <p className="text-xs text-cvp-warn" role="alert">
                  {err}
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
